import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { db, auth } from '../../../../firebase';
import { collection, addDoc, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const LavaRunner = ({ onClose }) => {
  const canvasRef      = useRef(null);
  const containerRef   = useRef(null); // measures available space for canvas
  const scoreRef       = useRef(0);
  const menuTimeoutRef = useRef(null);

  const [score,          setScore]          = useState(0);
  const [best,           setBest]           = useState(Number(localStorage.getItem('lr_best') || 0));
  const [leaderboard,    setLeaderboard]    = useState(JSON.parse(localStorage.getItem('lr_board') || '[]'));
  const [skinsUnlocked,  setSkinsUnlocked]  = useState(JSON.parse(localStorage.getItem('lr_skins') || '{}'));
  const [selectedSkin,   setSelectedSkin]   = useState(localStorage.getItem('lr_skin') || 'default');
  const [muted,          setMuted]          = useState(false);
  const [running,        setRunning]        = useState(false);
  const [showMenu,       setShowMenu]       = useState(true);
  const [fullscreen,     setFullscreen]     = useState(false);
  const [user,           setUser]           = useState(null);
  const [highScores,     setHighScores]     = useState([]);

  // ─── Game-loop variables (live inside effect closure) ────────────────────
  let audioCtx    = null;
  let beatTimerId = null;
  let W = 900, H = 480;
  let scrollSpeed   = 3;
  let gravity       = 0.55;
  let platformMinW  = 80, platformMaxW = 220;
  let gapMin = 80,  gapMax = 200;
  let difficultyStep   = 100;
  let nextDifficultyAt = difficultyStep;
  let platforms = [], obstacles = [], rocks = [], powerups = [];
  let player = { x: 140, y: 200, w: 28, h: 36, vy: 0, on: false, jumps: 0, maxJumps: 2, alive: true, skin: selectedSkin, shield: false };
  let lastTime   = 0;
  let beatInterval = 600;

  // ─── Firebase ────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) loadHighScores();
    });
    return unsub;
  }, []);

  const loadHighScores = async () => {
    try {
      const q = query(collection(db, 'GameZone/LavaRunner/scores'), orderBy('score', 'desc'), limit(5));
      const snap = await getDocs(q);
      setHighScores(snap.docs.map((d) => d.data()));
    } catch (e) { console.error('Error loading high scores:', e); }
  };

  const saveScoreToFirestore = async (s) => {
    if (!user || s <= 0) return;
    try {
      await addDoc(collection(db, 'GameZone/LavaRunner/scores'), {
        userId: user.uid,
        name: user.displayName || user.email || 'Anónimo',
        score: s,
        date: new Date().toISOString(),
      });
      loadHighScores();
    } catch (e) { console.error('Error saving score:', e); }
  };

  // ─── Audio ───────────────────────────────────────────────────────────────
  const initAudio = () => {
    try { audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { audioCtx = null; }
  };

  const playBeat = () => {
    if (!audioCtx || muted) return;
    const o = audioCtx.createOscillator(), g = audioCtx.createGain();
    o.type = 'sine'; o.frequency.value = 200; g.gain.value = 0.02;
    o.connect(g); g.connect(audioCtx.destination);
    o.start(); o.stop(audioCtx.currentTime + 0.06);
  };

  const updateBeatInterval = () => {
    beatInterval = Math.max(180, 600 - (scrollSpeed - 3) * 60);
    if (beatTimerId) { clearInterval(beatTimerId); beatTimerId = setInterval(playBeat, beatInterval); }
  };

  const playSfx = (name) => {
    if (muted || !audioCtx) return;
    try {
      const o = audioCtx.createOscillator(), g = audioCtx.createGain();
      o.connect(g); g.connect(audioCtx.destination);
      if (name === 'jump')  { o.frequency.value = 420; g.gain.value = 0.03; }
      else if (name === 'hit')   { o.frequency.value = 120; g.gain.value = 0.06; }
      else if (name === 'power') { o.frequency.value = 680; g.gain.value = 0.02; }
      o.start(); o.stop(audioCtx.currentTime + 0.08);
    } catch {}
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const rand = (a, b) => Math.random() * (b - a) + a;
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

  // ─── Platform generation ─────────────────────────────────────────────────
  const ensurePlatformsAhead = () => {
    let lastX = platforms.length ? platforms[platforms.length - 1].x + platforms[platforms.length - 1].w : 0;
    while (lastX < W * 2) {
      const w   = rand(platformMinW, platformMaxW);
      const gap = rand(gapMin, gapMax);
      // Platform Y is relative to H so it works at any canvas height
      let y = rand(H * 0.52, H * 0.80);
      if (Math.random() < 0.2) y = rand(H * 0.28, H * 0.60);
      const plat = { x: lastX + gap, y, w, h: 18, type: 'normal', broken: false };
      if (Math.random() < 0.18) plat.spikes   = true;
      if (Math.random() < 0.12) plat.crumbles = true;
      platforms.push(plat);
      if (Math.random() < 0.04) {
        powerups.push({ x: plat.x + rand(20, plat.w - 20), y: plat.y - 28, r: 10, type: pick(['shield', 'jump', 'slow']), ttl: 10000 });
      }
      if (Math.random() < 0.08) {
        obstacles.push({ x: plat.x + rand(10, plat.w - 30), y: plat.y - 14, w: 14, h: 14, type: 'spike' });
      }
      lastX = plat.x + plat.w;
    }
  };

  // ─── Reset ───────────────────────────────────────────────────────────────
  const resetGame = () => {
    scoreRef.current = 0; setScore(0);
    scrollSpeed = 3; platformMinW = 80; platformMaxW = 220;
    gapMin = 80; gapMax = 200; nextDifficultyAt = difficultyStep;
    platforms = []; obstacles = []; rocks = []; powerups = [];
    player = {
      x: 140,
      y: H * 0.42,   // relative to actual canvas height
      w: 28, h: 36, vy: 0, on: false, jumps: 0, maxJumps: 2,
      alive: true, skin: selectedSkin, shield: false,
    };
    // Seed initial safe platforms
    let x = 0;
    platforms.push({ x, y: H * 0.73, w: 300, h: 18, type: 'normal', broken: false });
    x += 300 + rand(20, 50);
    for (let i = 1; i < 6; i++) {
      const pw = rand(140, 240);
      platforms.push({ x, y: H * 0.73, w: pw, h: 18, type: 'normal', broken: false });
      x += pw + rand(20, 50);
    }
    ensurePlatformsAhead();
    if (audioCtx && !beatTimerId) beatTimerId = setInterval(playBeat, beatInterval);
  };

  // ─── Jump ────────────────────────────────────────────────────────────────
  const playerJump = () => {
    if (!player.alive) return;
    if (player.on) {
      player.vy = -11; player.on = false; player.jumps = 1; playSfx('jump');
    } else if (player.jumps < player.maxJumps) {
      player.vy = -10; player.jumps++; playSfx('jump');
    }
  };

  // ─── Update ──────────────────────────────────────────────────────────────
  const update = (delta) => {
    platforms.forEach((p) => (p.x -= scrollSpeed * delta));
    obstacles.forEach((o) => (o.x -= scrollSpeed * delta));
    powerups.forEach((pu) => (pu.x -= scrollSpeed * delta));
    rocks.forEach((r) => { r.y += r.vy * delta; r.vy += gravity * 0.6 * delta; r.x -= scrollSpeed * delta; });

    platforms = platforms.filter((p) => p.x + p.w > -200);
    obstacles = obstacles.filter((o) => o.x + (o.w || 12) > -200);
    powerups  = powerups.filter((p) => p.x > -200 && p.ttl > 0);
    rocks     = rocks.filter((r) => r.y < H + 200 && r.x > -300);
    ensurePlatformsAhead();

    player.vy += gravity * delta;
    player.y  += player.vy * delta;
    player.on  = false;

    for (const p of platforms) {
      if (player.x + player.w > p.x && player.x < p.x + p.w) {
        if (player.y + player.h > p.y && player.y + player.h < p.y + 20 && player.vy >= 0) {
          player.y = p.y - player.h; player.vy = 0; player.on = true; player.jumps = 0;
          if (p.crumbles && !p.crumbStart) p.crumbStart = performance.now();
          if (!p.touched) { p.touched = true; scoreRef.current += 5; setScore(scoreRef.current); }
        }
      }
    }
    for (const p of platforms) {
      if (p.crumbStart && performance.now() - p.crumbStart > 1000) p.broken = true;
    }
    platforms = platforms.filter((p) => !p.broken);

    for (const r of rocks) {
      if (rectOverlap(r, player)) {
        if (player.shield) { player.shield = false; playSfx('hit'); r.dead = true; }
        else playerDie('rock');
      }
    }
    for (const o of obstacles) {
      if (rectOverlap(o, player)) {
        if (player.shield) { player.shield = false; playSfx('hit'); o.dead = true; }
        else playerDie('spike');
      }
    }
    for (const pu of powerups) {
      pu.ttl -= 16 * delta;
      if (circleRectOverlap(pu, player)) { applyPowerup(pu); pu.collected = true; }
    }
    powerups   = powerups.filter((p) => !p.collected);
    rocks      = rocks.filter((r) => !r.dead);
    obstacles  = obstacles.filter((o) => !o.dead);

    if (player.y > H - 60) playerDie('lava');

    if (Math.random() < 0.01 + (scrollSpeed - 3) * 0.003) {
      rocks.push({ x: Math.random() * W + player.x - 80 + W, y: -40, w: 22, h: 22, vy: 0.4 + Math.random() * 1.2 });
    }

    scoreRef.current += 0.05 * scrollSpeed * delta;
    setScore(Math.floor(scoreRef.current));
    if (scoreRef.current >= nextDifficultyAt) { levelUp(); nextDifficultyAt += difficultyStep; }
  };

  const rectOverlap = (a, b) =>
    !(a.x + (a.w || 0) < b.x || a.x > b.x + b.w || a.y + (a.h || 0) < b.y || a.y > b.y + b.h);

  const circleRectOverlap = (c, r) => {
    const nx = Math.max(r.x, Math.min(c.x, r.x + r.w));
    const ny = Math.max(r.y, Math.min(c.y, r.y + r.h));
    const dx = c.x - nx, dy = c.y - ny;
    return dx * dx + dy * dy <= c.r * c.r;
  };

  const applyPowerup = (p) => {
    playSfx('power');
    if (p.type === 'shield') { player.shield = true; }
    else if (p.type === 'jump') { player.maxJumps = 3; setTimeout(() => { player.maxJumps = 2; }, 6000); }
    else if (p.type === 'slow') {
      const prev = scrollSpeed; scrollSpeed = Math.max(1.5, scrollSpeed * 0.5);
      setTimeout(() => { scrollSpeed = Math.min(prev + 0.2, prev * 1.02); }, 3000);
    }
    scoreRef.current += 20; setScore(Math.floor(scoreRef.current));
  };

  const playerDie = (kind) => {
    if (!player.alive) return;
    player.alive = false; playSfx('hit'); setRunning(false);
    const s = Math.floor(scoreRef.current);
    if (s > best) { setBest(s); localStorage.setItem('lr_best', s); }
    const nb = [...leaderboard, { score: s, date: new Date().toLocaleString() }]
      .sort((a, b) => b.score - a.score).slice(0, 10);
    setLeaderboard(nb); localStorage.setItem('lr_board', JSON.stringify(nb));
    unlockSkinsByScore(s); saveScoreToFirestore(s);
    if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
    menuTimeoutRef.current = setTimeout(() => setShowMenu(true), 800);
  };

  const unlockSkinsByScore = (s) => {
    const unlocks = { magma: 1000, ice: 2000, gold: 5000 };
    let changed = false; const ns = { ...skinsUnlocked };
    for (const k in unlocks) { if (s >= unlocks[k] && !ns[k]) { ns[k] = true; changed = true; } }
    if (changed) { setSkinsUnlocked(ns); localStorage.setItem('lr_skins', JSON.stringify(ns)); }
  };

  const levelUp = () => {
    platformMinW = Math.max(50, platformMinW - 6); platformMaxW = Math.max(90, platformMaxW - 12);
    gapMin += 6; gapMax += 12; scrollSpeed += 0.4; updateBeatInterval();
  };

  // ─── Render canvas ───────────────────────────────────────────────────────
  const render = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, '#08101a'); bg.addColorStop(1, '#0b0b0f');
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = 'rgba(255,80,0,0.06)'; ctx.fillRect(0, H - 80, W, 80);
    drawLava(H - 40);

    for (const p of platforms) {
      ctx.fillStyle = 'rgba(0,0,0,0.35)'; ctx.fillRect(p.x, p.y + 6, p.w, p.h);
      ctx.fillStyle = 'rgb(107,79,59)';   ctx.fillRect(p.x, p.y, p.w, p.h);
      if (p.spikes) drawSpikes(p.x, p.y, p.w);
    }
    for (const o of obstacles) { ctx.fillStyle = 'darkred'; ctx.fillRect(o.x, o.y, o.w, o.h); }
    for (const r of rocks) {
      ctx.fillStyle = '#7a6f64'; ctx.beginPath();
      ctx.ellipse(r.x + 11, r.y + 11, 11, 9, 0, 0, Math.PI * 2); ctx.fill();
    }
    for (const pu of powerups) {
      ctx.beginPath();
      ctx.fillStyle = pu.type === 'shield' ? 'rgba(80,200,255,0.9)' : pu.type === 'jump' ? 'rgba(180,80,255,0.9)' : 'rgba(120,255,120,0.9)';
      ctx.arc(pu.x, pu.y, pu.r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#111'; ctx.font = '10px monospace'; ctx.textAlign = 'center';
      ctx.fillText(pu.type[0].toUpperCase(), pu.x, pu.y + 3);
    }
    drawPlayer();
    ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.font = '11px monospace'; ctx.textAlign = 'right';
    ctx.fillText('Vel: ' + scrollSpeed.toFixed(1), W - 8, 16);
  };

  const drawPlayer = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.25)'; ctx.fillRect(player.x + 2, player.y + player.h + 8, player.w, 6);
    let color = '#ffd966';
    if (player.skin === 'magma') color = '#ff9a3c';
    else if (player.skin === 'ice') color = '#9fe6ff';
    else if (player.skin === 'gold') color = '#ffd700';
    ctx.fillStyle = color; ctx.fillRect(player.x, player.y, player.w, player.h);
    ctx.fillStyle = '#222';
    ctx.fillRect(player.x + 6,             player.y + 8, 6, 6);
    ctx.fillRect(player.x + player.w - 12, player.y + 8, 6, 6);
    if (player.shield) {
      ctx.strokeStyle = 'rgba(120,200,255,0.9)'; ctx.lineWidth = 3;
      ctx.strokeRect(player.x - 6, player.y - 6, player.w + 12, player.h + 12);
    }
    ctx.restore();
  };

  const drawSpikes = (x, y, w) => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = '#b22222'; const sw = 12;
    for (let sx = x; sx < x + w - 8; sx += sw) {
      ctx.beginPath(); ctx.moveTo(sx, y + 18); ctx.lineTo(sx + 6, y + 2); ctx.lineTo(sx + 12, y + 18); ctx.fill();
    }
  };

  const drawLava = (y) => {
    const ctx = canvasRef.current.getContext('2d');
    for (let i = 0; i < 6; i++) {
      ctx.fillStyle = `rgba(255,${100 + i * 20},0,${0.09 + i * 0.02})`;
      ctx.beginPath(); const amp = 12 + i * 4, freq = 0.01 + i * 0.004;
      ctx.moveTo(0, H);
      for (let x = 0; x <= W; x += 10) ctx.lineTo(x, y + Math.sin((x + performance.now() * 0.05) * freq) * amp);
      ctx.lineTo(W, H); ctx.closePath(); ctx.fill();
    }
  };

  // ─── Fullscreen ──────────────────────────────────────────────────────────
  const toggleFullscreen = () => {
    const el = document.documentElement;
    if (!fullscreen) {
      (el.requestFullscreen || el.mozRequestFullScreen || el.webkitRequestFullscreen || el.msRequestFullscreen || (() => {})).call(el);
    } else {
      (document.exitFullscreen || document.mozCancelFullScreen || document.webkitExitFullscreen || document.msExitFullscreen || (() => {})).call(document);
    }
  };

  useEffect(() => {
    const onFC = () => setFullscreen(!!(document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement));
    ['fullscreenchange', 'mozfullscreenchange', 'webkitfullscreenchange'].forEach((ev) => document.addEventListener(ev, onFC));
    return () => ['fullscreenchange', 'mozfullscreenchange', 'webkitfullscreenchange'].forEach((ev) => document.removeEventListener(ev, onFC));
  }, []);

  // ─── Input & resize ──────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.code === 'Space' || e.code === 'ArrowUp') {
      e.preventDefault();
      if (running) {
        playerJump();
      } else {
        if (menuTimeoutRef.current) { clearTimeout(menuTimeoutRef.current); menuTimeoutRef.current = null; }
        setShowMenu(false); setRunning(true); resetGame(); initAudio();
      }
    }
    if (e.code === 'KeyM') { setMuted((p) => !p); if (!muted && !audioCtx) initAudio(); }
  };

  const handleCanvasClick = () => { if (running) playerJump(); };
  const handleCanvasTouch = (e) => { e.preventDefault(); if (running) playerJump(); };

  // Resize based on the actual container element, not window dimensions
  const resize = () => {
    if (!canvasRef.current || !containerRef.current) return;
    const { width, height } = containerRef.current.getBoundingClientRect();
    if (!width || !height) return;
    canvasRef.current.width  = Math.floor(width);
    canvasRef.current.height = Math.floor(height);
    W = canvasRef.current.width;
    H = canvasRef.current.height;
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    if (canvasRef.current) {
      canvasRef.current.addEventListener('mousedown', handleCanvasClick);
      canvasRef.current.addEventListener('touchstart', handleCanvasTouch, { passive: false });
    }
    window.addEventListener('resize', resize);
    resize();
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (canvasRef.current) {
        canvasRef.current.removeEventListener('mousedown', handleCanvasClick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        canvasRef.current.removeEventListener('touchstart', handleCanvasTouch);
      }
      window.removeEventListener('resize', resize);
    };
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Game loop ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!running) return;
    let frameId;
    const loop = (t) => {
      if (!running) return;
      const dt = Math.min(40, t - lastTime); lastTime = t;
      update(dt / 16); render();
      frameId = requestAnimationFrame(loop);
    };
    frameId = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(frameId); if (beatTimerId) clearInterval(beatTimerId); };
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Start helpers ───────────────────────────────────────────────────────
  const startGame = () => {
    if (menuTimeoutRef.current) { clearTimeout(menuTimeoutRef.current); menuTimeoutRef.current = null; }
    setShowMenu(false); setRunning(true); resetGame(); initAudio();
  };

  // ─── Menu ────────────────────────────────────────────────────────────────
  const renderMenu = () => {
    const allSkins = ['default', 'magma', 'ice', 'gold'];
    return (
      <div className="bg-gradient-to-b from-[#111216] to-[#0b0f14] p-4 sm:p-5 rounded-xl text-gray-200 shadow-2xl w-[95vw] sm:w-[85vw] max-w-[860px] max-h-[85vh] overflow-y-auto">
        <h2 className="text-xl sm:text-2xl font-bold mb-2">🔥 Lava Runner</h2>
        <p className="mb-4 text-sm text-gray-400">
          Corre automático. Evita la lava. Cada 100 pts la dificultad sube.
        </p>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Scores */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm mb-1">Mejor local: <span className="text-yellow-400">{best}</span></p>
            <div className="max-h-[160px] overflow-auto mt-1">
              {leaderboard.length ? leaderboard.map((it, i) => (
                <div key={i} className="py-1 border-b border-dashed border-white/5 text-xs">
                  {i + 1}. <strong>{it.score}</strong> — <span className="opacity-60">{it.date}</span>
                </div>
              )) : <p className="text-xs opacity-60">Sin puntuaciones aún</p>}
            </div>
            {user ? (
              <div className="mt-3">
                <p className="font-semibold text-sm mb-1">Top 5 Global</p>
                <div className="max-h-[160px] overflow-auto">
                  {highScores.length ? highScores.map((hs, i) => (
                    <div key={i} className="py-1 border-b border-dashed border-white/5 text-xs">
                      {i + 1}. <strong>{hs.name}</strong>: {hs.score}
                    </div>
                  )) : <p className="text-xs opacity-60">Sin puntuaciones globales</p>}
                </div>
              </div>
            ) : <p className="mt-3 text-xs opacity-55">Inicia sesión para ver el ranking global</p>}
          </div>

          {/* Skins */}
          <div className="w-full md:w-64 flex-shrink-0">
            <p className="font-semibold text-sm mb-2">Skins (desbloquea con puntos)</p>
            <div className="flex flex-wrap gap-1">
              {allSkins.map((s) => {
                const locked = s !== 'default' && !skinsUnlocked[s];
                const cost   = s === 'magma' ? 1000 : s === 'ice' ? 2000 : s === 'gold' ? 5000 : 0;
                return (
                  <span
                    key={s}
                    className={`px-2 py-1 rounded-lg cursor-pointer border-2 text-xs ${locked ? 'opacity-45 grayscale' : ''} ${selectedSkin === s ? 'border-yellow-400' : 'border-transparent'}`}
                    onClick={() => {
                      if (skinsUnlocked[s] || s === 'default') { setSelectedSkin(s); localStorage.setItem('lr_skin', s); }
                      else Swal.fire({ toast: true, position: 'top-end', icon: 'info', title: `Necesitas ${cost} pts`, showConfirmButton: false, timer: 2500 });
                    }}
                  >
                    {s}{locked ? ` (${cost})` : ''}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <button className="bg-[#1b1f28] hover:bg-[#2b2f38] text-white px-3 py-2 rounded-lg text-sm" onClick={() => setShowMenu(false)}>Cerrar</button>
          <button className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg text-sm font-semibold" onClick={startGame}>▶ Jugar ahora</button>
          <button className="bg-[#1b1f28] hover:bg-[#2b2f38] text-white px-3 py-2 rounded-lg text-sm" onClick={toggleFullscreen}>
            {fullscreen ? '⛶ Salir' : '⛶ Pantalla completa'}
          </button>
        </div>
        <p className="text-gray-600 text-xs mt-2">Lava Runner by Antony Joseph for SIT.</p>
      </div>
    );
  };

  // ─── JSX ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col w-full h-full bg-[#08101a]">

      {/* ── Compact HUD ── */}
      <div className="flex items-center justify-between px-3 py-1.5 flex-shrink-0 bg-black/50 gap-2">
        <span className="text-white font-bold text-sm min-w-0 truncate">
          🔥 {Math.floor(score)}
        </span>

        {/* Controls hint — hidden on small screens */}
        <span className="hidden sm:block text-white/35 text-xs flex-1 text-center">
          Espacio / toca para saltar · doble salto
        </span>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            className="bg-white/10 hover:bg-white/20 text-white px-2.5 py-1 rounded text-xs font-semibold active:scale-95 transition-all"
            onClick={startGame}
          >
            ▶ Jugar
          </button>
          <button
            className="bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded text-xs active:scale-95 transition-all"
            onClick={() => { setMuted((p) => !p); if (!muted && !audioCtx) initAudio(); }}
          >
            {muted ? '🔇' : '🔊'}
          </button>
          <button
            className="hidden sm:block bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded text-xs active:scale-95 transition-all"
            onClick={() => {
              if (window.confirm('Resetear mejores puntuaciones?')) {
                setLeaderboard([]); localStorage.removeItem('lr_board'); localStorage.setItem('lr_best', 0); setBest(0);
              }
            }}
          >
            ↺
          </button>
        </div>
      </div>

      {/* ── Canvas — fills all remaining space ── */}
      <div ref={containerRef} className="flex-1 min-h-0 overflow-hidden relative w-full">
        <canvas
          ref={canvasRef}
          className="block w-full h-full"
          style={{ touchAction: 'none' }}
        />

        {/* Mobile tap hint overlay (shown only when game is running, touch device hint) */}
        {running && (
          <div
            className="sm:hidden absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none text-white/30 text-xs tracking-widest uppercase"
            style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}
          >
            Toca para saltar
          </div>
        )}
      </div>

      {/* ── Menu overlay ── */}
      {showMenu && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/75 z-20">
          {renderMenu()}
        </div>
      )}

      {/* ── Game over overlay ── */}
      {!showMenu && !running && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/75 z-20 text-center px-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Juego terminado</h1>
          <p className="text-lg sm:text-xl text-white/80 mb-6">Puntaje: {Math.floor(score)}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-lg font-semibold active:scale-95 transition-all" onClick={startGame}>
              ▶ Jugar otra vez
            </button>
            <button className="bg-white/10 hover:bg-white/20 text-white px-6 py-2.5 rounded-lg active:scale-95 transition-all" onClick={() => setShowMenu(true)}>
              Menú
            </button>
            {onClose && (
              <button className="bg-red-600/80 hover:bg-red-600 text-white px-6 py-2.5 rounded-lg active:scale-95 transition-all" onClick={() => { setRunning(false); setShowMenu(false); onClose(); }}>
                Salir
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LavaRunner;
